import React, { useEffect, useRef } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";

import "./services/axios-interceptor";

import PrivateRoute from "./components/private-route/private-route.component";
import "./App.scss";

import LoginPage from "./pages/login/login.page";
import LandingPage from "./pages/landing/landing.page";
import Saudha from "./pages/saudha/saudha.page";
import ApprovalDetails from "./pages/approvalDetails/details.page";

import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Dispatch } from "redux";
import { message, Modal } from "antd";
import {
  selectAuthData,
  selectIdleModal,
  selectIsLogged,
} from "./redux/user/user.selectors";
import {
  resetUser,
  setAuthData,
  setIdleModal,
} from "./redux/user/user.actions";
import CacheBuster from "./components/CacheBuster/CacheBuster.js";
import { checkSessionService, refreshService } from "./services/login-services";
import { User } from "./models";
import triggerSleepClient from "./services/timeout-services";
import { resetCommonReducer } from "./redux/common/common.actions";
import Offline from "./pages/offline/index.page";
import useTabGuard from "./utils/useTabDispatch";
import { dataConstants } from "./constants/data-constants";

message.config({
  maxCount: 1,
});

const App: React.FC = (props: any) => {
  const {
    isLogged,
    authData,
    setAuthValue,
    resetUserData,
    idleModal,
    resetReducer,
    setIdleModal,
  } = props;
  const history = useHistory();
  let timeout = useRef<NodeJS.Timeout>();
  let timer = useRef<NodeJS.Timeout>();
  let refreshCallTimer = useRef<NodeJS.Timeout>();
  let refreshErrorSignoutTimer = useRef<NodeJS.Timeout>();
  let instance = useRef<any>();
  const [modal, contextHolder] = Modal.useModal();
  let minutesToGo: number;
  useEffect(() => {
    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    const handlePageHide = (event) => {
      if (event.persisted) {
        return;
      }
      const token = authData.access_token;

      if (!token) {
        return;
      }
      if (!navEntries || navEntries == null || navEntries == undefined) {
        fetch(dataConstants.logoutUrl, {
          method: "POST",
          keepalive: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Api-Version": "1.5.0",
            "Client-Type": "web",
          },
        });
      }
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);
  useTabGuard();
  useEffect(() => {
    if (process.env.REACT_APP_ENV === "production") {
      (function (c, l, a, r, i, t?: any, y?: any) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", "vxjjmtvumt");
    } else {
      (function (c, l, a, r, i, t?: any, y?: any) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", "xxp63jico2");
    }
  }, []);
  const refreshTimer = () => {
    clearTimeout(timeout.current as NodeJS.Timeout);
    const callingTime =
      authData.access_token_expiry * 1000 -
      (authData.access_token_expiry * 1000) / 60;
    console.log(callingTime);
    timeout.current = setTimeout(() => {
      getRefreshFn();
    }, callingTime);
  };
  const checkSession = async () => {
    try {
      const checkResponse = await checkSessionService();
      if (checkResponse.status === 200) {
        // console.log(checkResponse?.data);
      }
    } catch (error: any) {
      if (error.response?.data?.msg == "Token has been revoked") {
        forcedLogout();
      } else {
        await getRefreshFn();
      }
    }
  };
  useEffect(() => {
    clearTimeout(timeout.current as NodeJS.Timeout);
    if (isLogged) {
      triggerSleepClient.onTouchStart(history);
      (async () => {
        // await getRefreshFn();
        refreshTimer();
      })();
    }
  }, [isLogged]);

  useEffect(() => {
    if (isLogged && authData.refresh_token) {
      refreshTimer();
      minutesToGo =
        (authData.refresh_token_expiry - authData.access_token_expiry * 1000) /
        60;
    } else {
      instance?.current?.destroy();
      clearTimeout(timeout.current as NodeJS.Timeout);
      clearInterval(timer.current as NodeJS.Timeout);
      clearInterval(refreshCallTimer.current as NodeJS.Timeout);
    }
  }, [isLogged, authData.refresh_token]);
  useEffect(() => {
    let refreshIntervalID;
    if (isLogged) {
      refreshIntervalID = setInterval(() => {
        checkSession();
      }, 5000);
    }
    return () => {
      clearInterval(refreshIntervalID);
    };
  }, [isLogged, authData.access_token]);
  const forcedLogout = () => {
    triggerSleepClient.stopTimers();
    clearTimeout(timeout.current as NodeJS.Timeout);
    clearInterval(timer.current as NodeJS.Timeout);
    clearInterval(refreshCallTimer.current as NodeJS.Timeout);
    setIdleModal();
    instance.current?.destroy();
    localStorage.clear();
    sessionStorage.clear();
    resetUserData();
    resetReducer();
    history.push("/login");
  };

  const getRefreshFn = async () => {
    try {
      const refreshResponse = await refreshService({
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
      });
      setAuthValue({
        ...authData,
        access_token: refreshResponse.data.data.access_token,
        refresh_token: refreshResponse.data.data.refresh_token,
        access_token_expiry: refreshResponse.data.data.access_token_expiry,
        refresh_token_expiry: refreshResponse.data.data.refresh_token_expiry,
      });
      clearInterval(timer.current as NodeJS.Timeout);
      clearInterval(refreshCallTimer.current as NodeJS.Timeout);
      clearTimeout(refreshErrorSignoutTimer.current as NodeJS.Timeout);
      instance.current && instance.current.destroy();
      !triggerSleepClient.idleLogoutTimer &&
        !triggerSleepClient.idleAlertModalTimer &&
        !triggerSleepClient.titleInterval &&
        triggerSleepClient.onInit(history);
    } catch (error: any) {
      if (error.response?.data?.msg == "Token has been revoked") {
        forcedLogout();
        return false;
      }
      if (!instance.current && !idleModal) {
        triggerSleepClient.stopTimers();
        instance.current = modal.warning({
          title: "Connection Alert",
          content: `There is a problem connecting to the server , Please try to refresh the application.`,
          closable: false,
          okButtonProps: {
            style: { border: 0 },
            danger: true,
            type: "primary",
          },
          keyboard: false,
          mask: true,
          centered: true,
          okText: "Logout",
          onOk: () => forcedLogout(),
        });

        timer.current = setInterval(() => {
          minutesToGo -= 1;
          instance.current.update({
            content: `There is a problem connecting to the server , Please try to refresh the application.`,
          });
        }, 60000);

        refreshCallTimer.current = setInterval(() => {
          (async () => await getRefreshFn())();
        }, 180000);

        refreshErrorSignoutTimer.current = setTimeout(() => {
          forcedLogout();
        }, minutesToGo * 60000);
      }
    }
  };
  return (
    <>
      {contextHolder}
      <Modal
        className="temporal-modal"
        title="Session Alert"
        open={idleModal}
        closable={false}
        maskClosable={false}
        okText="Continue"
        cancelText="Log Out"
        style={{ textAlign: "center" }}
        styles={{
          mask: { opacity: "1" },
        }}
        centered
        okButtonProps={{
          style: { backgroundColor: "#20346a", border: 0 },
        }}
        cancelButtonProps={{
          danger: true,
          // type: "ghost",
        }}
        onOk={() => {
          (async () => {
            await triggerSleepClient.onTouchStart(history);
            getRefreshFn();
          })();
        }}
        onCancel={() => triggerSleepClient.consentLogoutUser(history)}
      >
        <div style={{ color: "black" }}>
          Your session is about to expire. Do you want to continue session?
        </div>
      </Modal>
      <div
        onMouseMove={() => isLogged && triggerSleepClient.onTouchStart(history)}
      >
        <CacheBuster>
          {({ loading, isLatestVersion, refreshCacheAndReload }: any) => {
            if (loading) return null;
            if (!loading && !isLatestVersion) {
              refreshCacheAndReload();
            }
            return (
              <Offline>
                <Switch>
                  <Route
                    exact
                    path="/login"
                    render={() =>
                      isLogged ? <Redirect to="/TN/home" /> : <LoginPage />
                    }
                  />
                  {/* <Route
                    exact
                    path="/sauda"
                    render={() =>
                      isLogged ? <Saudha /> : <Redirect to="/login" />
                    }
                  /> */}
                  <Route
                    exact
                    path="/details"
                    render={() =>
                      isLogged ? <ApprovalDetails /> : <Redirect to="/login" />
                    }
                  />
                  <PrivateRoute path="/TN" component={LandingPage} />
                  <Redirect exact from="*" to="/login" />
                </Switch>
              </Offline>
            );
          }}
        </CacheBuster>
      </div>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  isLogged: selectIsLogged,
  authData: selectAuthData,
  idleModal: selectIdleModal,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setAuthValue: (authData: User) => dispatch(setAuthData(authData)),
    setIdleModal: () => dispatch(setIdleModal(false)),
    resetReducer: () => dispatch(resetCommonReducer()),
    resetUserData: () => dispatch(resetUser()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
